import React, { useState, useCallback } from 'react'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import {
  RadioGroup,
  Button,
  DatePicker,
  Select,
  cn,
  TravelerSelection,
  type ITravelerInfo
} from '@pcln/horizon'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import type { QueryString, TripType } from '@/types'
import type { GA4PageNameType } from '@/ga4types'
import { getDateFieldErrorMessage } from '@/shared-utils/error-helpers'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import useTabGroup from '@/hooks/useTabGroup'
import { HorizonFlyDatePicker } from '@pcln/horizon-fly-date-picker'
import useInitialFlightFormValues from './useInitialFlightFormValues'
import useFlightFormSubmitValues from './useFlightFormSubmitValues'
import searchFormSchema from '../SearchForm/validation'
import {
  getGridContainerClass,
  getFlightTypeClass,
  getInputClass
} from './HorizonFlightForm.variants'
import {
  CABIN_OPTIONS,
  PLACEHOLDERS,
  LABELS,
  DEFAULT_VALUES
} from './constants'
import { MULTI_DESTINATION, ONE_WAY, ROUND_TRIP } from '../constants'

import {
  onCalendarChange,
  createRadioOptions,
  getSelectedDates,
  getMultiDestinationSelectedDate,
  addFlightOnClick,
  getMultiDestDefaultMonth,
  getMultiDestDisabledDates
} from './utils/formHandlers'
import type {
  CABIN_CLASS_TYPE,
  FLIGHT_TYPE,
  FlightFormStateType
} from '../types'
import { isRoundTrip } from '../SearchForm/utils'

type FlightSearchFormProps = {
  isRebook?: boolean
  isModify?: boolean
  onSubmit: (
    pageName: GA4PageNameType,
    data: FlightFormStateType,
    queryParams?: QueryString
  ) => void
  pageName?: GA4PageNameType
}

export default function FlightSearchForm({
  isRebook = false,
  isModify = false,
  onSubmit,
  pageName = 'homepage'
}: FlightSearchFormProps) {
  const { initialValues, query, isMobile, cguid, appName, appVersion } =
    useInitialFlightFormValues(isRebook, isModify)

  const {
    control,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm<FlightFormStateType>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const {
    watchFlightType,
    watchFlights,
    watchCabinClass,
    watchTravelers,
    watchTripType
  } = useFlightFormSubmitValues({
    control,
    clearErrors,
    initialValues
  })

  const flightDetails = {
    cabinClass: watchCabinClass || DEFAULT_VALUES.CABIN_CLASS,
    tripType: watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE,
    flights: [
      {
        departureAirport: {
          id: watchFlights[0]?.startLocation?.id || '',
          cityCode: watchFlights[0]?.startLocation?.cityCode || ''
        },
        arrivalAirport: {
          id: watchFlights[0]?.endLocation?.id || '',
          cityCode: watchFlights[0]?.endLocation?.cityCode || ''
        }
      }
    ]
  }

  const [addHotel, setAddHotel] = useState<boolean>(DEFAULT_VALUES.ADD_HOTEL)
  const [addCar, setAddCar] = useState<boolean>(DEFAULT_VALUES.ADD_CAR)
  const { registerTabElement } = useTabGroup()

  const flights = watchFlights || [
    {
      startLocation: {},
      endLocation: {},
      startDate: '',
      endDate: ''
    }
  ]

  const isPackage = addHotel || addCar

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    STAY: addHotel,
    DRIVE: addCar
  })

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections(prev => ({
        ...prev,
        [optionValue]: checked
      }))
      setValue('tripType', updatedTripType as TripType)
      if (optionValue === 'STAY') {
        setAddHotel(checked)
        const newRooms = checked ? 1 : 0
        setValue('travelers', {
          ...watchTravelers,
          rooms: newRooms
        })
      } else if (optionValue === 'DRIVE') {
        setAddCar(checked)
      }
    },
    [setValue, watchTravelers]
  )
  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const [defaultMonth, setDefaultMonth] = useState<Date>(currentDate)

  return (
    <div className="w-auto">
      <form
        onSubmit={handleSubmit((values: FlightFormStateType) => {
          onSubmit?.(pageName, values, query)
          logSearchFormAnalytics(cguid, pageName, 'flights', isMobile)
        })}
        className="w-auto"
        aria-label="horizon-flight-search-form"
      >
        <div className="w-auto mx-auto">
          <div
            className={getGridContainerClass(
              watchFlightType,
              addHotel,
              isPackage
            )}
          >
            {/* Flight Type Radio Buttons */}
            <div
              className={getFlightTypeClass(
                watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE
              )}
            >
              <RadioGroup
                className="flex-row gap-4"
                name="flightType"
                options={createRadioOptions()}
                defaultValue="round-trip"
                required
                aria-labelledby="flight-type"
                handleChange={(value: unknown) => {
                  setValue('flightType', value as TripType)
                }}
              />
              {/* Cabin class dropdown - conditionally rendered */}
              {!isPackage && (
                <div className="lg:block hidden">
                  <Select
                    size="sm"
                    label={LABELS.CABIN_CLASS}
                    menuItems={[...CABIN_OPTIONS]}
                    initialValue={watchCabinClass || DEFAULT_VALUES.CABIN_CLASS}
                    onChange={(value: string) => {
                      setValue('cabinClass', value as CABIN_CLASS_TYPE, {
                        shouldDirty: true
                      })
                    }}
                    placeholder={PLACEHOLDERS.CABIN_CLASS}
                    isLabelHidden
                  />
                </div>
              )}
            </div>

            {/* Multi-destination flights rendering */}
            {watchFlightType === MULTI_DESTINATION ? (
              <>
                {flights.map((flight, index) => (
                  <React.Fragment
                    key={`flight-${
                      flight.startLocation?.id ||
                      flight.endLocation?.id ||
                      index
                    }`}
                  >
                    <div className="col-span-full flex items-center gap-4">
                      <span className="font-bold text-base lg:text-sm">
                        Flight {index + 1}
                      </span>
                      {index > 0 && (
                        <Button
                          type="tertiary"
                          size="sm"
                          buttonType="button"
                          className="**:text-xs **:font-medium min-h-auto"
                          onClick={() => {
                            // Remove flight from form state
                            const updatedFlights = [
                              ...flights.slice(0, index),
                              ...flights.slice(index + 1)
                            ]
                            setValue('flights', updatedFlights)
                          }}
                          aria-label={`Remove flight ${index + 1}`}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    {/* Origin Input */}
                    <AutocompleteWrapper
                      searchKey="startLocation"
                      label="Departing from?"
                      typeAheadEndpoint="flights"
                      errorMessage={
                        errors.flights?.[index]?.startLocation?.message
                      }
                      initialSelectedItem={flight.startLocation}
                      onItemSelect={itemSelected => {
                        setValue(
                          `flights.${index}.startLocation`,
                          itemSelected ?? undefined
                        )
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                      }}
                    />

                    {/* Destination Input */}
                    <AutocompleteWrapper
                      searchKey="endLocation"
                      label="Going to?"
                      typeAheadEndpoint="flights"
                      errorMessage={
                        errors.flights?.[index]?.endLocation?.message
                      }
                      initialSelectedItem={flight.endLocation}
                      onItemSelect={itemSelected => {
                        setValue(
                          `flights.${index}.endLocation`,
                          itemSelected ?? undefined
                        )
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                      }}
                    />

                    {/* Departure Date */}
                    <div className="**:translate-y-0">
                      <DatePicker
                        isMobile={isMobile}
                        className="flight-dates"
                        title="Flights: Departure"
                        label="Departing"
                        mode="single"
                        numberOfMonths={isMobile ? 12 : 2}
                        selected={getMultiDestinationSelectedDate(
                          index,
                          flights
                        )}
                        errorMessage={getDateFieldErrorMessage(
                          errors,
                          0,
                          isRoundTrip(watchFlightType)
                        )}
                        handleCalendarChange={dates => {
                          onCalendarChange(
                            dates,
                            setDefaultMonth,
                            setValue,
                            index
                          )
                        }}
                        disabled={{
                          before: getMultiDestDisabledDates(
                            flights[index - 1]?.startDate
                          )
                        }}
                        startMonth={currentDate}
                        endMonth={calendarMaxMonth}
                        defaultMonth={getMultiDestDefaultMonth(
                          flights[index - 1]?.startDate,
                          flight.startDate
                        )}
                      />
                    </div>
                  </React.Fragment>
                ))}

                {/* Add another flight button */}
                <div className="col-span-full lg:[grid-area:unset]">
                  <Button
                    type="secondary"
                    size="sm"
                    buttonType="button"
                    onClick={() => addFlightOnClick(setValue, watchFlights)}
                    disabled={watchFlights && watchFlights.length >= 5}
                  >
                    Add Another Flight
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Single/Round-trip flight inputs */}
                <div className={getInputClass('origin')}>
                  <AutocompleteWrapper
                    searchKey="startLocation"
                    label="Departing from?"
                    typeAheadEndpoint="flights"
                    errorMessage={errors.flights?.[0]?.startLocation?.message}
                    initialSelectedItem={flights[0]?.startLocation}
                    onItemSelect={itemSelected => {
                      setValue(
                        'flights.0.startLocation',
                        itemSelected ?? undefined
                      )
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                  />
                </div>

                <div className={getInputClass('destination')}>
                  <AutocompleteWrapper
                    searchKey="endLocation"
                    label="Going to?"
                    typeAheadEndpoint="flights"
                    initialSelectedItem={flights[0]?.endLocation}
                    errorMessage={errors.flights?.[0]?.endLocation?.message}
                    onItemSelect={itemSelected => {
                      setValue(
                        'flights.0.endLocation',
                        itemSelected ?? undefined
                      )
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                  />
                </div>

                <div
                  className={getInputClass(
                    'timeline',
                    watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE
                  )}
                >
                  <HorizonFlyDatePicker
                    isMobile={isMobile}
                    key={watchFlightType}
                    className="flight-dates"
                    title={
                      (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) ===
                      ROUND_TRIP
                        ? 'Flights: Departing – Returning'
                        : 'Flights: Departing'
                    }
                    label={
                      (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) ===
                      ROUND_TRIP
                        ? 'Departing – Returning'
                        : 'Departing'
                    }
                    mode={
                      (watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE) ===
                      ROUND_TRIP
                        ? 'range'
                        : 'single'
                    }
                    numberOfMonths={isMobile ? 12 : 2}
                    selected={getSelectedDates(
                      0,
                      flights,
                      watchFlightType as FLIGHT_TYPE,
                      DEFAULT_VALUES.FLIGHT_TYPE
                    )}
                    errorMessage={getDateFieldErrorMessage(
                      errors,
                      0,
                      isRoundTrip(watchFlightType as FLIGHT_TYPE)
                    )}
                    handleCalendarChange={dates => {
                      onCalendarChange(dates, setDefaultMonth, setValue, 0)
                      if (
                        addHotel &&
                        dates &&
                        'from' in dates &&
                        dates.from &&
                        dates?.to === undefined
                      ) {
                        setMaxSelectableDate(addDays(dates.from, 28))
                      } else {
                        setMaxSelectableDate(undefined)
                      }
                    }}
                    inputRef={registerTabElement}
                    flightDetails={flightDetails}
                    apolloClientInfo={{
                      clientName: appName,
                      clientVersion: appVersion
                    }}
                    disabled={{
                      before: currentDate,
                      after: maxSelectableDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultMonth}
                    allowSameDayInRange={!addHotel}
                  />
                </div>
              </>
            )}

            {/* Room with Passengers /Passengers only Input (when hotel package is added) */}
            <div
              className={getInputClass(
                'passengers',
                watchFlightType || DEFAULT_VALUES.FLIGHT_TYPE
              )}
            >
              <Controller
                control={control}
                name="travelers"
                defaultValue={watchTravelers}
                render={({ field }) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { ref, ...rest } = field
                  return (
                    <TravelerSelection
                      {...rest}
                      isMobile={isMobile}
                      isHotel={addHotel}
                      roomsEnabled={addHotel}
                      tooltipErrorMessageId="travelerSelectionError"
                      errorMessage={
                        errors?.travelers?.lapInfants?.message || ''
                      }
                      initialValues={field.value}
                      shouldCountLapInfant
                      maxChildren={6}
                      isTravelerSelectionDisabled={isRebook || isModify}
                      onChange={(values: ITravelerInfo) => {
                        field.onChange({
                          adults: values.adults,
                          children: values.children,
                          lapInfants: values.lapInfants,
                          childrenAges: values.childrenAges,
                          rooms: values.rooms
                        })
                      }}
                    />
                  )
                }}
              />
            </div>

            {/* Cabin Class - conditionally rendered */}
            {!isPackage && (
              <Select
                label={LABELS.CABIN_CLASS}
                isInset
                iconLeft="airline_seat_recline_normal"
                menuItems={[...CABIN_OPTIONS]}
                initialValue={watchCabinClass}
                placeholder={PLACEHOLDERS.CABIN_CLASS}
                className={getInputClass('cabin', watchFlightType)}
                onChange={(value: string) => {
                  setValue('cabinClass', value as CABIN_CLASS_TYPE, {
                    shouldDirty: true
                  })
                }}
              />
            )}

            {/* Package Deals */}
            {watchFlightType === ROUND_TRIP && (
              <div className="flex flex-row items-center justify-start gap-2 text-sm lg:[grid-area:auto/1/auto/-1] lg:order-last [grid-area:unset] sm:[grid-area:deals]">
                <HorizonBundleAndSave
                  productType="flights"
                  checkedValues={bundleSelections}
                  onSelectionChange={handleBundleSelectionChange}
                  tripType={watchTripType}
                />
              </div>
            )}

            {/* Search Button */}
            <Button
              type="primary"
              size="lg"
              buttonType="submit"
              className={cn(
                '[grid-area:unset] lg:[grid-area:unset]',
                watchFlightType === MULTI_DESTINATION
                  ? 'sm:[grid-area:unset]'
                  : 'sm:[grid-area:submit]',
                watchFlightType === ONE_WAY && 'sm:col-span-full',
                isMobile ? 'fixed bottom-4 right-4 left-4' : ''
              )}
            >
              {PLACEHOLDERS.SEARCH_BUTTON}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
